import { useState, useEffect, useRef } from 'react';

const API = import.meta.env.VITE_API_BASE || '/api';

// Session-level cache, one summary per NORAD ID per browser session
const summaryCache = new Map();

function buildPrompt(sat, detail) {
  const parts = [];
  parts.push(`Satellite: ${sat.name}`);
  if (detail?.cospar_id)                   parts.push(`COSPAR: ${detail.cospar_id}`);
  if (sat.category)                        parts.push(`Category: ${sat.category}`);
  if (detail?.operator || sat.operator)    parts.push(`Operator: ${detail?.operator || sat.operator}`);
  if (detail?.country_code || sat.country) parts.push(`Country: ${detail?.country_code || sat.country}`);
  if (detail?.ucs_users)                   parts.push(`Users: ${detail.ucs_users}`);
  if (detail?.ucs_purpose)                 parts.push(`Purpose: ${detail.ucs_purpose}`);
  if (detail?.ucs_detailed_purpose)        parts.push(`Detailed purpose: ${detail.ucs_detailed_purpose}`);
  if (detail?.ucs_comments)               parts.push(`Notes: ${detail.ucs_comments}`);
  if (sat.alt_km)                          parts.push(`Altitude: ${sat.alt_km.toFixed(0)} km`);
  if (sat.period_min)                      parts.push(`Period: ${sat.period_min} min`);
  if (sat.inclination)                     parts.push(`Inclination: ${sat.inclination.toFixed(1)}°`);
  if (detail?.apogee_km)                   parts.push(`Apogee/Perigee: ${detail.apogee_km}/${detail.perigee_km} km`);
  if (detail?.launch_mass_kg)              parts.push(`Mass: ${detail.launch_mass_kg} kg`);
  if (detail?.expected_life_yrs)           parts.push(`Design life: ${detail.expected_life_yrs} years`);
  if (detail?.object_type)                 parts.push(`Type: ${detail.object_type}`);
  if (detail?.launch_date) parts.push(`Launch year: ${new Date(detail.launch_date).getFullYear()}`);
  if (detail?.launch_site)    parts.push(`Launch site: ${detail.launch_site}`);
  if (detail?.launch_vehicle) parts.push(`Vehicle: ${detail.launch_vehicle}`);
  if (detail?.contractor)     parts.push(`Contractor: ${detail.contractor}`);
  return parts.join('\n');
}

export default function AISummary({ sat, detail, variant }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(false);
  const abortRef = useRef(null);

  const skip = !sat?.norad_id ||
    ['aviation', 'groundstation', 'launches'].includes(sat.category);

  useEffect(() => {
    if (skip) { setSummary(null); return; }
    const key = sat.norad_id;
    if (summaryCache.has(key)) { setSummary(summaryCache.get(key)); return; }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true); setSummary(null); setError(false);

    fetch(`${API}/ai/summary`, {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ context: buildPrompt(sat, detail), norad_id: sat.norad_id, category: sat.category }),
    })
      .then(r => r.json())
      .then(data => {
        const text = data?.summary?.trim();
        if (text) { summaryCache.set(key, text); setSummary(text); }
        else setError(true);
        setLoading(false);
      })
      .catch(e => {
        if (e.name !== 'AbortError') setError(true);
        setLoading(false);
      });

    return () => controller.abort();
  }, [sat?.norad_id, !!detail]);

  if (skip) return null;

  // Editorial variant, serif body text with no boxed chrome, for the v2
  // FeaturedV2 analyst note.
  if (variant === 'editorial') {
    if (loading && !summary) {
      return <span className="v2-note-body" style={{ fontStyle: 'italic', color: 'var(--txt-dim)' }}>Analyzing…</span>;
    }
    if (summary) {
      return <span className="v2-note-body">{summary}</span>;
    }
    return <span className="v2-note-body" style={{ color: 'var(--txt-dim)' }}>Summary unavailable.</span>;
  }

  return (
    <div style={{
      margin: '8px 0 12px', padding: '10px 12px',
      background: 'rgba(255,215,0,0.04)',
      border: '1px solid rgba(255,215,0,0.14)',
      borderRadius: 8, fontFamily: 'monospace',
    }}>
      {loading && !summary && (
        <div style={{ fontSize: 11, color: '#7a93b0', fontStyle: 'italic' }}>◌ analyzing…</div>
      )}
      {summary && (
        <div style={{ fontSize: 12, color: '#c0d4e8', lineHeight: 1.7 }}>{summary}</div>
      )}
      {error && !summary && (
        <div style={{ fontSize: 11, color: '#7a93b0' }}>- summary unavailable</div>
      )}
    </div>
  );
}
